import { Link } from "react-router";
import { ArrowLeft, MoreHorizontal, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { formatApiError } from "@/lib/format-api-error";
import { formatRelative } from "@/lib/utils";
import { useJourneyAssignments } from "./use-journey-assignments";
import { useUnassignJourney } from "./use-unassign-journey";
import { AssignPartnersDialog } from "./assign-partners-dialog";
import type { JourneyAssignmentAdminRow } from "./types";

// Phase 7 sub-bucket 14 — Assignments tab inside the journey builder.
//
// Single table of journey_assignments × partners with a per-row progress
// aggregate (X / Y completed). useUnassignJourney is row-scoped (confirm
// dialog handled inline below). Assign partners is a Dialog with a multi-
// select checkbox list (AssignPartnersDialog) — mirrors the Next.js
// AssignJourneyDialog.

export function JourneyAssignmentsTab({ journeyId }: { journeyId: string }) {
  const query = useJourneyAssignments(journeyId);
  const rows = query.data ?? [];
  const assignedPartnerIds = new Set(rows.map((r) => r.partner_id));

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold tracking-tight">
          Assignments
        </h2>
        <AssignPartnersDialog
          journeyId={journeyId}
          assignedPartnerIds={assignedPartnerIds}
        />
      </div>

      {query.isLoading ? (
        <TableSkeleton />
      ) : query.isError ? (
        <EmptyState
          icon={ArrowLeft}
          title="Couldn't load assignments"
          description={query.error?.message ?? "Try refreshing."}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No partners assigned yet"
          description="Use Assign partners above to bulk-assign this journey. Each assignment spawns one progress row per task."
        />
      ) : (
        <AssignmentsTable journeyId={journeyId} rows={rows} />
      )}
    </div>
  );
}

function AssignmentsTable({
  journeyId,
  rows,
}: {
  journeyId: string;
  rows: JourneyAssignmentAdminRow[];
}) {
  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            <th className="px-4 py-2.5">Partner</th>
            <th className="px-4 py-2.5 text-right">Progress</th>
            <th className="px-4 py-2.5 text-right">Assigned</th>
            <th className="px-4 py-2.5 text-right">Completed</th>
            <th className="w-10" />
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">
          {rows.map((r) => (
            <AssignmentRow key={r.id} journeyId={journeyId} row={r} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AssignmentRow({
  journeyId,
  row,
}: {
  journeyId: string;
  row: JourneyAssignmentAdminRow;
}) {
  const unassign = useUnassignJourney();
  const busy = unassign.isPending;

  function handleUnassign() {
    unassign.mutate(
      { journeyId, assignmentId: row.id },
      {
        onSuccess: () => toast.success("Partner unassigned"),
        onError: (err) => toast.error(formatApiError(err, "Failed to unassign")),
      },
    );
  }

  const { complete, total } = row.progress;
  const ratio = total === 0 ? 0 : complete / total;
  const percent = Math.round(ratio * 100);

  return (
    <tr className="group transition-colors hover:bg-muted/30">
      <td className="px-4 py-3">
        <Link
          to={`/admin/partners/${row.partner_id}`}
          className="font-medium text-foreground hover:text-accent"
        >
          {row.partner_name}
        </Link>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="ml-auto flex max-w-[180px] items-center justify-end gap-2">
          <div
            className="h-1.5 w-20 overflow-hidden rounded-full bg-muted"
            aria-hidden="true"
          >
            <div
              className="h-full bg-status-active"
              style={{ width: `${percent}%` }}
            />
          </div>
          <span className="tabular-nums text-xs text-muted-foreground">
            {complete} / {total}
          </span>
        </div>
      </td>
      <td className="px-4 py-3 text-right text-xs text-muted-foreground">
        {formatRelative(row.assigned_at)}
      </td>
      <td className="px-4 py-3 text-right text-xs text-muted-foreground">
        {row.completed_at ? formatRelative(row.completed_at) : "—"}
      </td>
      <td className="px-4 py-3 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              aria-label={`Actions for ${row.partner_name}`}
              disabled={busy}
              className="opacity-0 transition group-hover:opacity-100 data-[state=open]:opacity-100"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to={`/admin/partners/${row.partner_id}`}>
                Open partner
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleUnassign}
              disabled={busy}
              className="text-destructive focus:text-destructive"
            >
              Unassign journey
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}

function TableSkeleton() {
  return (
    <div className="rounded-xl border bg-card">
      <div className="space-y-3 p-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-4">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="ml-auto h-4 w-24" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}
